import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, X, Wrench, IndianRupee, Paperclip, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * ServicePOActionModal — Phase 6, `service_po_review` (Rahul.K).
 *
 * Rules (mirror TransportPOActionModal):
 *   • Approve  → `service_approx_price` MANDATORY (alphanumeric); remarks OPTIONAL.
 *                Optional file upload (quote / bill) attached with kind=service_quote.
 *   • Hold/Reject → remarks MANDATORY (min 2 chars); price/attachment ignored.
 */
const PRICE_RE = /^[A-Za-z0-9 ,.\-₹$/()+]+$/;

export default function ServicePOActionModal({ open, onClose, onDone, requisition, action, currentUser, actingAsBanner = null }) {
  const [remarks, setRemarks] = useState("");
  const [price, setPrice] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setPrice(""); setFile(null); setError("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, action, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const cfg = {
    approve: { label: "Approve", pastTense: "Approved", Icon: CheckCircle2, tone: "purple",
               hint: "Enter the approximate price you quoted for this service engagement." },
    on_hold: { label: "Place on Hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold",
               hint: "The applicant will be asked to revise the requisition." },
    reject:  { label: "Reject", pastTense: "Rejected", Icon: XCircle, tone: "danger",
               hint: "The requisition will be closed with your reason." },
  }[action];
  if (!cfg) return null;

  const trimmedRemarks = remarks.trim();
  const trimmedPrice = price.trim();
  const remarksMandatory = action !== "approve";
  const remarksOk = true; // Phase 12.1 — no length rule
  const priceOk = action !== "approve"
    ? true
    : trimmedPrice.length > 0 && PRICE_RE.test(trimmedPrice);
  const canSubmit = remarksOk && priceOk && !busy;

  const btnCls = {
    purple: "bg-[#6b3fa0] hover:bg-[#4a2b70] border-[#4a2b70]",
    gold:   "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger: "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const uploadAttachment = async () => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("field_name", "service_po_attachment");
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const payload = { action, remarks: trimmedRemarks };
      if (action === "approve") {
        payload.service_approx_price = trimmedPrice;
        if (file) {
          try {
            const att = await uploadAttachment();
            if (att?.url) {
              payload.attachments = [{
                field_name: "service_po_attachment",
                filename: att.filename, url: att.url, size: att.size,
                kind: "service_quote",
              }];
            }
          } catch {
            toast.warning("Attachment upload failed — approving without it.");
          }
        }
      }
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      toast.success(`${cfg.pastTense} — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="service-po-modal">
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Wrench className="text-[#6b3fa0]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Service PO · {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong> on behalf of the assigned approver. Logged as a super-admin bypass.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#6b3fa0] px-3 py-2 rounded">
            <strong>{cfg.hint}</strong>
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">{error}</div>
          )}

          {action === "approve" && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Approx Price <span className="text-[#b3261e]">*</span>
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">alphanumeric</span>
                </span>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                  <input type="text" value={price} onChange={(e) => setPrice(e.target.value.slice(0, 120))}
                         placeholder="e.g. ₹ 45,000"
                         className={`input-vgws pl-9 ${trimmedPrice && !priceOk ? "!border-[#b3261e]" : (trimmedPrice && priceOk) ? "!border-[#6b3fa0]" : ""}`}
                         data-testid="service-approx-price" />
                </div>
                {trimmedPrice && !priceOk && (
                  <div className="mt-1 text-xs text-[#b3261e]">Only alphanumeric plus space and ,.-₹$/()+ are allowed.</div>
                )}
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Attachment (quote / SoW)
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">optional</span>
                </span>
                <div className="flex items-center gap-2">
                  <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                         accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                         className="text-xs text-[#4a524c]" data-testid="service-po-attachment" />
                  {file && (
                    <span className="text-xs text-[#6b3fa0] inline-flex items-center gap-1">
                      <Paperclip size={12} /> {file.name}
                    </span>
                  )}
                </div>
              </label>
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {remarksMandatory
                ? <span className="text-[#b3261e]">*</span>
                : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
              {remarksMandatory && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 2 chars</span>}
            </span>
            <textarea ref={textareaRef} value={remarks} onChange={(e) => setRemarks(e.target.value.slice(0, 1000))} rows={4}
                      placeholder={remarksMandatory ? "Describe your reason clearly…" : "Add any note for the record (optional)…"}
                      className={`input-vgws min-h-[100px] ${remarksMandatory && trimmedRemarks && !remarksOk ? "!border-[#b3261e]" : ""}`}
                      data-testid="service-po-remarks" />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={remarksMandatory && !remarksOk ? "text-[#b3261e]" : "text-[#6b3fa0]"}>
                {remarksMandatory
                  ? (remarksOk ? "Looks good." : "Mandatory for hold/reject (min 2 characters).")
                  : "Optional for approve."}
              </span>
              <span className="font-mono text-[#6e7770]">{trimmedRemarks.length} / 1000</span>
            </div>
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="service-po-cancel">Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${btnCls} ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="service-po-confirm">
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <cfg.Icon size={16} />}
            {busy ? "Please wait…" : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
